import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import Loader from 'shared/feedback/Loader';
import Pagination from 'shared/data-display/Pagination';
import ConfirmationModal from 'shared/overlays/ConfirmationModal';
import { useAdminUsers, useRevokeToken } from 'entities/user';
import { useUser } from 'app/state/contexts';
import type { UserOrganization } from 'app/state/contexts/UserContext';
import './AdminUsers.scss';

const PAGE_SIZE = 20;

interface AdminUser {
  id: string;
  firstName?: string;
  lastName?: string;
  userName?: string;
  email?: string[];
  attributeRoles?: { attributeId: number }[];
}

const getUserEmail = (user: AdminUser): string => {
  if (user.email && user.email.length > 0) {
    return user.email[0];
  }
  return user.userName ?? '';
};

const AdminUsers = () => {
  const { t } = useTranslation(['admin-users', 'common', 'reset']);
  const { organizations = [] } = useUser();

  const [searchText, setSearchText] = useState('');
  const [selectedOrgs, setSelectedOrgs] = useState<number[]>([]);
  const [page, setPage] = useState(0);
  const [revokeUserId, setRevokeUserId] = useState<string | null>(null);

  const { data: allUsers, isLoading } = useAdminUsers();
  const revokeToken = useRevokeToken();

  const filteredItems = useMemo(() => {
    let items = (allUsers as AdminUser[] | undefined) ?? [];

    if (searchText) {
      const search = searchText.toLowerCase();
      items = items.filter((user) => {
        const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.toLowerCase();
        const email = getUserEmail(user).toLowerCase();
        return name.includes(search) || email.includes(search);
      });
    }

    if (selectedOrgs.length > 0) {
      items = items.filter((user) => user.attributeRoles?.some((ar) => selectedOrgs.includes(ar.attributeId)));
    }

    return items;
  }, [allUsers, searchText, selectedOrgs]);

  const paginatedItems = useMemo(() => {
    const start = page * PAGE_SIZE;
    return filteredItems.slice(start, start + PAGE_SIZE);
  }, [filteredItems, page]);

  const paginationPage = useMemo(
    () => ({
      number: page,
      totalPages: Math.ceil(filteredItems.length / PAGE_SIZE),
      totalElements: filteredItems.length,
      size: PAGE_SIZE,
    }),
    [filteredItems, page]
  );

  const onReset = () => {
    setSearchText('');
    setSelectedOrgs([]);
    setPage(0);
  };

  const toggleOrg = (orgId: number) => {
    setSelectedOrgs((prev) => (prev.includes(orgId) ? prev.filter((id) => id !== orgId) : [...prev, orgId]));
  };

  const getUserOrganizations = (user: AdminUser): string => {
    if (!user.attributeRoles) return '';
    const orgIds = user.attributeRoles.map((ar) => ar.attributeId).filter(Boolean);
    const orgNames = orgIds.map((id) => organizations.find((org) => org.id === id)?.name).filter(Boolean);
    return orgNames.join(', ');
  };

  const handleRevoke = () => {
    if (revokeUserId) {
      revokeToken.mutate(revokeUserId, {
        onSettled: () => setRevokeUserId(null),
      });
    }
  };

  const renderContent = () => {
    if (isLoading || !allUsers) {
      return <Loader />;
    }

    return (
      <>
        <table className="admin-users-table">
          <thead>
            <tr>
              <th>{t('column.name')}</th>
              <th>{t('column.email')}</th>
              <th>{t('column.organizations')}</th>
              <th>{t('column.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {paginatedItems.map((user) => (
              <tr key={user.id}>
                <td>
                  {user.firstName} {user.lastName}
                </td>
                <td>{getUserEmail(user)}</td>
                <td>{getUserOrganizations(user)}</td>
                <td>
                  <button className="btn btn-sm btn-danger" onClick={() => setRevokeUserId(user.id)}>
                    {t('revoke.button')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination onPaginate={(p) => setPage(p)} page={paginationPage} pageSize={PAGE_SIZE} />
      </>
    );
  };

  return (
    <>
      <div className="content-title columns two mb-2">
        <h1>{t('title')}</h1>
      </div>
      <form
        className="search-field-wrapper row align-items-end"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(0);
        }}
      >
        <div className="search-field form-group col">
          <label className="search-field-label">{t('search.placeholder')}</label>
          <input className="form-control" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
        </div>
        <div className="search-field form-group col">
          <label className="search-field-label">{t('filter.organization')}</label>
          <Dropdown autoClose="outside">
            <Dropdown.Toggle variant="outline-primary" className="form-control text-start d-flex justify-content-between align-items-center">
              {selectedOrgs.length > 0
                ? organizations
                    .filter((org) => selectedOrgs.includes(org.id))
                    .map((org) => org.name)
                    .join(', ')
                : t('filter.organization')}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {organizations.map((org: UserOrganization) => (
                <Dropdown.Item key={org.id} onClick={() => toggleOrg(org.id)} active={selectedOrgs.includes(org.id)}>
                  <input type="checkbox" checked={selectedOrgs.includes(org.id)} readOnly className="me-2" />
                  {org.name}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <div className="col">
          <button className="btn btn-primary me-2">{t('common:search')}</button>
          <button className="btn btn-secondary" type="button" onClick={onReset}>
            {t('reset:field')}
          </button>
        </div>
      </form>
      {renderContent()}
      <ConfirmationModal
        show={revokeUserId !== null}
        message={t('revoke.confirm')}
        onConfirm={handleRevoke}
        onDecline={() => setRevokeUserId(null)}
        busy={revokeToken.isPending}
        centered
      />
    </>
  );
};

export default AdminUsers;
